import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import QuestionReports from './pages/QuestionReports';
import Questions from './pages/Questions';
import QuestionView from './pages/QuestionView';
import QuestionEdit from './pages/QuestionEdit';
import QuestionImportExport from './pages/QuestionImportExport';
import ReviewQueue from './pages/ReviewQueue';
import Subjects from './pages/Subjects';
import ReferralCodes from './pages/ReferralCodes';
import Users from './pages/Users';
import Exams from './pages/Exams';
import Analytics from './pages/Analytics';

// EduNxtAi design system — color tokens derived from the logo:
//   - Primary teal:  #338275 (logo text + icon)
//   - Gold accent:   #D4A017 (logo "AI")
const theme = createTheme({
  palette: {
    primary: {
      main: '#338275',
      light: '#E8F3F0',
      dark: '#1F5449',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#D4A017',
      light: '#FBF3DC',
      dark: '#8A6800',
      contrastText: '#4A3600',
    },
    success: {
      main: '#10B981',
      light: '#E6F7F0',
      dark: '#047857',
    },
    warning: {
      main: '#F59E0B',
      light: '#FEF3E0',
      dark: '#B47500',
    },
    error: {
      main: '#E05555',
      light: '#FDECEC',
      dark: '#B83838',
    },
    info: {
      main: '#0EA5E9',
      light: '#E0F4FC',
      dark: '#0277B2',
    },
    background: {
      default: '#FAF8F2',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#1A3A34',
      secondary: '#5A6D6B',
      disabled: '#8A9A97',
    },
    divider: '#E5E7E5',
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: { fontWeight: 800, letterSpacing: '-0.02em' },
    h2: { fontWeight: 800, letterSpacing: '-0.01em' },
    h3: { fontWeight: 700 },
    h4: { fontWeight: 700 },
    h5: { fontWeight: 700 },
    h6: { fontWeight: 700 },
    button: { fontWeight: 600 },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 10,
          fontWeight: 600,
          boxShadow: 'none',
          '&:hover': { boxShadow: '0 4px 12px rgba(51, 130, 117, 0.15)' },
        },
        containedPrimary: {
          background: 'linear-gradient(135deg, #338275 0%, #1F5449 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #2A6F64 0%, #174238 100%)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 1px 2px rgba(26, 58, 52, 0.04)',
          border: '1px solid #F0F2F1',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600, borderRadius: 999 },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundColor: '#F5F7F6',
          '& fieldset': { borderColor: '#E5E7E5' },
          '&:hover fieldset': { borderColor: '#5FA896' },
          '&.Mui-focused fieldset': { borderColor: '#338275', borderWidth: 2 },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        colorPrimary: {
          background: 'linear-gradient(135deg, #338275 0%, #1F5449 100%)',
        },
      },
    },
  },
});

// Placeholder components (to be implemented)
const Profile = () => <div>Profile Page - Coming Soon</div>;
const Settings = () => <div>Settings Page - Coming Soon</div>;

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<Login />} />

            {/* Protected Routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/questions"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Questions />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/subjects"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Subjects />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/questions/new"
              element={
                <ProtectedRoute>
                  <Layout>
                    <QuestionEdit />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/questions/:id"
              element={
                <ProtectedRoute>
                  <Layout>
                    <QuestionView />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/questions/:id/edit"
              element={
                <ProtectedRoute>
                  <Layout>
                    <QuestionEdit />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/questions/review"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ReviewQueue />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/questions/import-export"
              element={
                <ProtectedRoute>
                  <Layout>
                    <QuestionImportExport />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/question-reports"
              element={
                <ProtectedRoute>
                  <Layout>
                    <QuestionReports />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/referral-codes"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ReferralCodes />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/exams"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Exams />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/users"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Users />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/analytics"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Analytics />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Profile />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Settings />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Default Route */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />

            {/* 404 Route */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
